import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, ChevronRight, DollarSign, Users, Calendar, FileText,
  CheckCircle2, ArrowRight, ExternalLink, Info
} from 'lucide-react';

import { fetchBeasiswa } from '../utils/api';

// Dipakai sebagai tampilan awal sebelum data dari API tiba, sekaligus cadangan
// bila API sedang tidak dapat dihubungi.
const SCHOLARSHIPS = [
  // Internal
  {
    id: 1,
    type: 'internal',
    name: 'Beasiswa Prestasi Akademik STIKES HI',
    value: 'Bebas SPP 1 Semester',
    quota: '10 mahasiswa/semester',
    org: 'STIKES Harapan Ibu Jambi',
    deadline: 'Setiap awal semester',
    requirements: [
      'Mahasiswa aktif STIKES HI Jambi minimal semester 2',
      'IPK minimal 3.50 pada semester sebelumnya',
      'Tidak sedang menerima beasiswa lain',
      'Berkelakuan baik dan tidak pernah kena sanksi akademik',
    ],
    benefits: ['Pembebasan SPP 1 semester penuh', 'Sertifikat penghargaan'],
    color: 'purple',
  },
  {
    id: 2,
    type: 'internal',
    name: 'Beasiswa Mahasiswa Berprestasi Non-Akademik',
    value: 'Keringanan SPP 50%',
    quota: '5 mahasiswa/semester',
    org: 'STIKES Harapan Ibu Jambi',
    deadline: 'Setiap awal semester',
    requirements: [
      'Aktif dalam kegiatan UKM atau organisasi kemahasiswaan',
      'Memiliki prestasi di tingkat regional/nasional',
      'IPK minimal 3.00',
      'Rekomendasi dari Pembina UKM/Organisasi',
    ],
    benefits: ['Keringanan SPP 50%', 'Piagam penghargaan', 'Prioritas ikut lomba bersama kampus'],
    color: 'purple',
  },
  {
    id: 3,
    type: 'internal',
    name: 'Beasiswa Keluarga Tidak Mampu',
    value: 'Keringanan SPP 25-75%',
    quota: 'Sesuai ketersediaan',
    org: 'STIKES Harapan Ibu Jambi',
    deadline: 'Setiap semester',
    requirements: [
      'Surat keterangan tidak mampu dari kelurahan/desa',
      'Fotokopi kartu BPJS PBI atau DTKS',
      'IPK minimal 2.75',
      'Tidak sedang bekerja penuh waktu',
    ],
    benefits: ['Keringanan SPP 25%, 50%, atau 75%', 'Konsultasi beasiswa lain'],
    color: 'purple',
  },
  // External
  {
    id: 4,
    type: 'eksternal',
    name: 'Beasiswa KIP Kuliah Kemendikbud',
    value: 'Biaya Hidup + Biaya Kuliah',
    quota: 'Kuota nasional',
    org: 'Kemendikbud RI',
    deadline: 'Februari – Maret (setiap tahun)',
    requirements: [
      'Mahasiswa baru atau mahasiswa aktif semester 1-6',
      'Berasal dari keluarga kurang mampu (data DTKS/PKH/KKS/PIP)',
      'Lulus seleksi administratif dan wawancara',
      'Terdaftar di PD-Dikti',
    ],
    benefits: ['Pembebasan biaya pendidikan penuh', 'Bantuan biaya hidup Rp 700.000–1.400.000/bulan'],
    color: 'blue',
    link: 'https://kip-kuliah.kemdikbud.go.id',
  },
  {
    id: 5,
    type: 'eksternal',
    name: 'Beasiswa Pemerintah Provinsi Jambi',
    value: 'Rp 1.500.000/bulan',
    quota: 'Terbatas, sesuai APBD',
    org: 'Pemerintah Provinsi Jambi',
    deadline: 'April – Mei (setiap tahun)',
    requirements: [
      'WNI domisili Provinsi Jambi',
      'Mahasiswa aktif semester 2 ke atas',
      'IPK minimal 3.00',
      'Tidak sedang menerima beasiswa pemerintah lain',
      'Surat rekomendasi dari pimpinan perguruan tinggi',
    ],
    benefits: ['Biaya hidup Rp 1.500.000/bulan selama 12 bulan', 'Pembinaan dan monitoring rutin'],
    color: 'green',
  },
  {
    id: 6,
    type: 'eksternal',
    name: 'Beasiswa Bank Indonesia (BI) Genbi',
    value: 'Rp 1.500.000/bulan',
    quota: '5-10 mahasiswa/tahun',
    org: 'Bank Indonesia',
    deadline: 'Juli – Agustus (setiap tahun)',
    requirements: [
      'Mahasiswa aktif semester 2 – 6',
      'IPK minimal 3.00',
      'Berprestasi dan aktif berorganisasi',
      'Tidak sedang menerima beasiswa lain',
      'Lulus seleksi wawancara',
    ],
    benefits: ['Bantuan biaya pendidikan Rp 1.500.000/bulan', 'Bergabung komunitas GenBI nasional', 'Program pengembangan diri dan leadership'],
    color: 'green',
  },
  {
    id: 7,
    type: 'eksternal',
    name: 'Beasiswa Tanoto Foundation',
    value: 'Beasiswa Penuh',
    quota: 'Seleksi nasional',
    org: 'Tanoto Foundation',
    deadline: 'September – Oktober (setiap tahun)',
    requirements: [
      'Mahasiswa aktif semester 2 ke atas',
      'IPK minimal 3.00',
      'Terlibat aktif dalam kegiatan sosial kemasyarakatan',
      'Berkomitmen berkontribusi bagi masyarakat',
    ],
    benefits: ['Beasiswa penuh (biaya kuliah + biaya hidup)', 'Mentoring dan program pengembangan', 'Jaringan alumni nasional Tanoto'],
    color: 'green',
    link: 'https://www.tanotofoundation.org',
  },
];

const COLOR_MAP = {
  purple: {
    badge: 'bg-purple-100 dark:bg-purple-900/30 text-[#7B2FBE] dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800/40',
    accent: 'border-l-purple-400',
    icon: 'text-[#7B2FBE] bg-purple-50 dark:bg-purple-900/30',
  },
  blue: {
    badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800/40',
    accent: 'border-l-blue-400',
    icon: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
  },
  green: {
    badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800/40',
    accent: 'border-l-green-400',
    icon: 'text-green-600 bg-green-50 dark:bg-green-900/30',
  },
};

const ScholarshipCard = ({ s }) => {
  const [open, setOpen] = useState(false);
  const c = COLOR_MAP[s.color];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      data-testid={`scholarship-card-${s.id}`}
      className={`bg-white dark:bg-[#1A182E] rounded-2xl border-l-4 ${c.accent} border ${c.border} shadow-sm hover:shadow-lg transition-all duration-300`}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.icon}`}>
              <DollarSign size={18} />
            </div>
            <div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.badge} mb-1.5 inline-block`}>
                {s.type === 'internal' ? 'Beasiswa Internal' : 'Beasiswa Eksternal'}
              </span>
              <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading text-sm leading-tight">{s.name}</h3>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <DollarSign size={12} className="text-[#7B2FBE]" />
            <span>{s.value}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Users size={12} className="text-[#7B2FBE]" />
            <span>{s.quota}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <FileText size={12} className="text-[#7B2FBE]" />
            <span className="truncate">{s.org}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Calendar size={12} className="text-[#7B2FBE]" />
            <span>{s.deadline}</span>
          </div>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="text-xs text-[#7B2FBE] dark:text-purple-400 font-medium hover:underline flex items-center gap-1"
        >
          {open ? 'Sembunyikan detail' : 'Lihat syarat & manfaat'}
          <ChevronRight size={12} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>

        {open && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Persyaratan:</p>
              <ul className="space-y-1.5">
                {s.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <CheckCircle2 size={12} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Manfaat:</p>
              <ul className="space-y-1.5">
                {s.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <ArrowRight size={12} className="text-green-500 flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            {s.link && (
              <a
                href={s.link}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#7B2FBE] border border-[#7B2FBE] px-3 py-1.5 rounded-lg hover:bg-[#7B2FBE] hover:text-white transition-colors"
              >
                <ExternalLink size={12} /> Website Resmi
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Beasiswa = () => {
  const [activeType, setActiveType] = useState('semua');
  const [scholarships, setScholarships] = useState(SCHOLARSHIPS);

  useEffect(() => {
    let active = true;
    fetchBeasiswa()
      .then((data) => {
        if (active && Array.isArray(data) && data.length) setScholarships(data);
      })
      .catch((error) => console.warn('Menggunakan data beasiswa lokal:', error.message));
    return () => { active = false; };
  }, []);

  const filtered = activeType === 'semua' ? scholarships : scholarships.filter(s => s.type === activeType);
  const internalCount = scholarships.filter(s => s.type === 'internal').length;
  const externalCount = scholarships.filter(s => s.type === 'eksternal').length;

  return (
    <div data-testid="beasiswa-page">
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              <span className="text-white/70">Akademik</span>
              <ChevronRight size={14} />
              <span className="text-white">Beasiswa</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">Beasiswa</h1>
            <p className="text-white/80 max-w-xl text-base">
              Berbagai program beasiswa tersedia untuk mendukung mahasiswa berprestasi dan kurang mampu di STIKES HI Jambi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
              { val: scholarships.length, label: 'Total Beasiswa', icon: <DollarSign size={18} /> },
              { val: internalCount, label: 'Beasiswa Internal', icon: <FileText size={18} /> },
              { val: externalCount, label: 'Beasiswa Eksternal', icon: <ExternalLink size={18} /> },
            ].map((s, i) => (
              <div key={i} className="bg-white dark:bg-[#1A182E] rounded-2xl p-5 text-center border border-gray-100 dark:border-gray-800 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-[#7B2FBE] flex items-center justify-center mx-auto mb-2">{s.icon}</div>
                <div className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">{s.val}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Filter */}
          <div className="flex gap-2 mb-8 flex-wrap">
            {[
              { key: 'semua', label: 'Semua Beasiswa' },
              { key: 'internal', label: 'Beasiswa Internal' },
              { key: 'eksternal', label: 'Beasiswa Eksternal' },
            ].map(f => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setActiveType(f.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeType === f.key
                    ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
                    : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#7B2FBE]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-12">
            {filtered.map(s => <ScholarshipCard key={s.id} s={s} />)}
          </div>

          {/* Info Banner */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/40 rounded-2xl p-6 flex items-start gap-4">
            <Info size={20} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-[#7B2FBE] dark:text-purple-300 mb-2 font-heading">Butuh Informasi Lebih Lanjut?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                Untuk informasi pendaftaran beasiswa, persyaratan, dan jadwal terbaru, hubungi Bagian Kemahasiswaan STIKES HI Jambi.
              </p>
              <Link
                to="/kontak"
                className="inline-flex items-center gap-2 bg-[#7B2FBE] text-white px-5 py-2 rounded-xl text-sm font-medium hover:bg-[#9333EA] transition-colors"
              >
                Hubungi Kemahasiswaan <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Beasiswa;
